// Shared, premium change-request presentation used by BOTH the client
// Access-Management approvals and the PFA Approvals page. Centralizes:
//   * money/percent/date field formatting (INR: ₹22 K / ₹22 L / ₹22 Cr)
//   * the "key facts" a human actually cares about per module
//     (assets → type + value, liabilities → type + outstanding + EMI,
//      insurance → type + cover + premium, goals → target + date)
//   * a rich detail body (key-fact hero + full field diff)
import React from "react";
import {
  Wallet,
  TrendingDown,
  ShieldCheck,
  Target,
  FileText,
  LayoutDashboard,
} from "lucide-react";
import { compactINR, formatINR } from "@/lib/currency";

export const MODULE_META = {
  assets: { label: "Assets", icon: Wallet, cls: "bg-emerald-500 text-white", accent: "emerald" },
  liabilities: { label: "Liabilities", icon: TrendingDown, cls: "bg-rose-500 text-white", accent: "rose" },
  insurance: { label: "Insurance", icon: ShieldCheck, cls: "bg-sky-500 text-white", accent: "sky" },
  goals: { label: "Goals", icon: Target, cls: "bg-violet-500 text-white", accent: "violet" },
  documents: { label: "Documents", icon: FileText, cls: "bg-orange-500 text-white", accent: "orange" },
  dashboard: { label: "Dashboard", icon: LayoutDashboard, cls: "bg-slate-600 text-white", accent: "slate" },
};

export const OP_META = {
  create: { label: "Add", verb: "add", cls: "text-emerald-700 bg-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-300", bar: "bg-emerald-400" },
  update: { label: "Edit", verb: "update", cls: "text-teal-700 bg-teal-100 dark:bg-teal-500/15 dark:text-teal-300", bar: "bg-teal-400" },
  delete: { label: "Remove", verb: "remove", cls: "text-rose-700 bg-rose-100 dark:bg-rose-500/15 dark:text-rose-300", bar: "bg-rose-400" },
};

const HIDDEN_FIELDS = [
  "id", "user_id", "created_at", "updated_at", "is_active", "has_tenure",
  "has_cash_value", "is_retirement", "is_emi", "is_liquid", "synthetic",
  "source", "advisor_user_id",
];

// Money fields get INR formatting; percents get "%"; dates get a friendly date.
const MONEY_FIELDS = new Set([
  "value", "purchase_value", "outstanding", "original_amount", "emi",
  "prepayment_amount", "sum_assured", "premium_amount", "current_value",
  "maturity_value", "target_amount", "current_amount", "monthly_contribution",
  "principal", "credit_limit",
]);
const PERCENT_FIELDS = new Set([
  "expected_return", "interest_rate", "inflation_rate", "contribution_step_up",
]);
const DATE_FIELDS = new Set([
  "target_date", "purchase_date", "start_date", "end_date", "maturity_date",
  "settlement_date",
]);

const TITLE = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());

export function fmtField(key, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (MONEY_FIELDS.has(key)) return compactINR(value);
  if (PERCENT_FIELDS.has(key)) return `${value}%`;
  if (DATE_FIELDS.has(key)) {
    const d = new Date(value);
    return isNaN(d.getTime())
      ? String(value)
      : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  }
  if (typeof value === "number") return value.toLocaleString("en-IN");
  return TITLE(String(value));
}

export function reqTitle(req) {
  return (
    (req.payload && (req.payload.name || req.payload.title)) ||
    (req.before && (req.before.name || req.before.title)) ||
    req.summary ||
    `${MODULE_META[req.module]?.label || req.module} record`
  );
}

// The 2–3 facts a person scans for. Reads from payload (create/update) then
// before (delete). Returns [{ label, value, strong? }].
export function keyFacts(req) {
  const d = req.payload || req.before || {};
  const facts = [];
  const push = (label, key) => {
    if (d[key] !== undefined && d[key] !== null && d[key] !== "")
      facts.push({ label, value: fmtField(key, d[key]), key });
  };
  switch (req.module) {
    case "assets":
      if (d.category) facts.push({ label: "Type", value: TITLE(d.category) });
      push("Value", "value");
      push("Return", "expected_return");
      break;
    case "liabilities":
      if (d.category) facts.push({ label: "Type", value: TITLE(d.category) });
      push("Outstanding", "outstanding");
      push("EMI", "emi");
      break;
    case "insurance":
      if (d.policy_type) facts.push({ label: "Type", value: TITLE(d.policy_type) });
      push("Cover", "sum_assured");
      push("Premium", "premium_amount");
      break;
    case "goals":
      push("Target", "target_amount");
      push("By", "target_date");
      push("Saved", "current_amount");
      break;
    case "documents":
      if (d.category) facts.push({ label: "Category", value: TITLE(d.category) });
      break;
    default:
      break;
  }
  return facts;
}

// Compact inline facts (used on cards).
export function KeyFactsRow({ req }) {
  const facts = keyFacts(req);
  if (!facts.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {facts.map((f) => (
        <span
          key={f.label}
          className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-0.5 text-[11px]"
        >
          <span className="text-slate-400">{f.label}</span>
          <span className="font-semibold text-slate-700 dark:text-slate-200">
            {f.value}
          </span>
        </span>
      ))}
    </div>
  );
}

// The rich detail body — key-fact hero tiles + full field diff. Reused inside
// any modal (client approve view / advisor request view).
export function ChangeRequestDetail({ req }) {
  const after = req.payload || {};
  const before = req.before || {};
  const facts = keyFacts(req);

  if (req.operation === "delete") {
    return (
      <div className="space-y-4">
        {facts.length > 0 && <FactTiles facts={facts} />}
        <div className="rounded-2xl border border-rose-200 dark:border-rose-500/20 bg-rose-50 dark:bg-rose-500/5 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">
          This will permanently remove{" "}
          <b>{before.name || before.title || "this record"}</b>.
        </div>
      </div>
    );
  }

  const keys = Object.keys(after).filter((k) => !HIDDEN_FIELDS.includes(k));
  const rows = keys.filter((k) =>
    req.operation === "update" ? String(before[k]) !== String(after[k]) : true,
  );

  return (
    <div className="space-y-4">
      {facts.length > 0 && <FactTiles facts={facts} />}
      {rows.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
            {req.operation === "update" ? "What changes" : "Details"}
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
            {rows.slice(0, 16).map((k) => (
              <div key={k} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span className="w-32 shrink-0 text-xs text-slate-400">
                  {TITLE(k)}
                </span>
                {req.operation === "update" ? (
                  <span className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="line-through text-slate-400">
                      {fmtField(k, before[k])}
                    </span>
                    <span className="text-slate-300">→</span>
                    <b className="text-slate-800 dark:text-slate-100">
                      {fmtField(k, after[k])}
                    </b>
                  </span>
                ) : (
                  <b className="text-slate-800 dark:text-slate-100 truncate">
                    {fmtField(k, after[k])}
                  </b>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FactTiles({ facts }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
      {facts.map((f) => (
        <div
          key={f.label}
          className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/40 px-3.5 py-2.5"
        >
          <div className="text-[10px] uppercase tracking-wider text-slate-400">
            {f.label}
          </div>
          <div className="mt-0.5 text-sm font-bold text-slate-900 dark:text-white truncate">
            {f.value}
          </div>
        </div>
      ))}
    </div>
  );
}

export { formatINR, compactINR };
